import { useCallback, useRef, useState } from 'react';

export interface HistoryAction {
  type: 'node-move' | 'node-delete' | 'nodes-delete' | 'edge-delete';
  description: string;
  undo: () => Promise<void>;
  redo: () => Promise<void>;
}

interface UseCanvasHistoryReturn {
  pushAction: (action: HistoryAction) => void;
  undo: () => Promise<void>;
  redo: () => Promise<void>;
  canUndo: boolean;
  canRedo: boolean;
  isUndoing: boolean;
  isRedoing: boolean;
}

const MAX_HISTORY = 10;

export function useCanvasHistory(): UseCanvasHistoryReturn {
  const undoStackRef = useRef<HistoryAction[]>([]);
  const redoStackRef = useRef<HistoryAction[]>([]);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [isUndoing, setIsUndoing] = useState(false);
  const [isRedoing, setIsRedoing] = useState(false);

  const updateState = useCallback(() => {
    setCanUndo(undoStackRef.current.length > 0);
    setCanRedo(redoStackRef.current.length > 0);
  }, []);

  const pushAction = useCallback((action: HistoryAction) => {
    undoStackRef.current.push(action);
    // Limit history size
    if (undoStackRef.current.length > MAX_HISTORY) {
      undoStackRef.current.shift();
    }
    // Clear redo stack on new action
    redoStackRef.current = [];
    updateState();
  }, [updateState]);

  const undo = useCallback(async () => {
    const action = undoStackRef.current.pop();
    if (!action) return;

    setIsUndoing(true);
    try {
      await action.undo();
      redoStackRef.current.push(action);
    } catch (err) {
      console.error('Undo failed:', err);
      // Put action back on undo stack if it failed
      undoStackRef.current.push(action);
    } finally {
      setIsUndoing(false);
      updateState();
    }
  }, [updateState]);

  const redo = useCallback(async () => {
    const action = redoStackRef.current.pop();
    if (!action) return;

    setIsRedoing(true);
    try {
      await action.redo();
      undoStackRef.current.push(action);
    } catch (err) {
      console.error('Redo failed:', err);
      // Put action back on redo stack if it failed
      redoStackRef.current.push(action);
    } finally {
      setIsRedoing(false);
      updateState();
    }
  }, [updateState]);

  return {
    pushAction,
    undo,
    redo,
    canUndo,
    canRedo,
    isUndoing,
    isRedoing,
  };
}
